import React from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n';
import { capitalizeWords } from 'TCIUtils';
import { TCITooltip } from '@/components/ui/Tooltip';
import styles from './AssignmentsTable.module.scss';

export const ASSIGNMENT_DETAILS_TOOLTIP_CLASS = 'assignment-details-tooltip-icon';
export const PAST_DUE_LOCK_TOOLTIP_CLASS = 'assignment-past-due-lock-icon';

export const AssignmentNotStartedInfoIcon = () => {
  const label = I18n.t('assignment_not_started');

  return (
    <TCITooltip>
      <TCITooltip.Trigger asChild>
        <i aria-label={label} className="fa fa-info-circle" role="img" tabIndex={0} />
      </TCITooltip.Trigger>
      <TCITooltip.Content>{label}</TCITooltip.Content>
    </TCITooltip>
  );
};

export const PastEnforcedDueDateLockIcon = () => {
  const label = I18n.t('assignment_past_enforced_due_date');

  return (
    <TCITooltip>
      <TCITooltip.Trigger asChild>
        <i aria-label={label} className="fa fa-lock" role="img" tabIndex={0} />
      </TCITooltip.Trigger>
      <TCITooltip.Content>{label}</TCITooltip.Content>
    </TCITooltip>
  );
};

const divideSections = (sections) => {
  if (!sections) {
    return [null, []];
  }

  const streams = sections.filter(section => section.stream);
  const text = sections.filter(section => !section.stream);

  return [streams, text];
};

const filterNotebooks = (notebooks) => {
  if (!notebooks) {
    return [];
  }

  return notebooks.filter(notebook => notebook.unit_project);
};

const getReadingNotebooks = (notebooks, assignedReadingSections) => {
  if (!notebooks) return [];

  // only show reading notebooks link if the reading text has not been assigned
  return notebooks.filter(notebook => (!notebook.unit_project && !notebook.video_activity &&
    !assignedReadingSections?.find(assignedReading => assignedReading.id === notebook.parent_section)));
};

const getReadingGames = (readingGames, assignedReadingSections) => {
  if (!readingGames) return [];

  // only show reading games link if the reading text has not been assigned
  return readingGames.filter(
    readingGame => !assignedReadingSections?.find(assignedReading => assignedReading.id === readingGame.parent_section)
  );
};

const filterVideoActivities = (notebooks, slideShows) => {
  if (!notebooks && !slideShows) return [];

  const assignables = (notebooks || []).concat(slideShows || []);

  return assignables.filter(assignable => (assignable && assignable.video_activity));
};

const filterClassroomActivities = (slideShows) => {
  if (!slideShows) return [];

  return slideShows.filter(ss => ss.classroom_activity);
};

const stripHtml = (htmlString) => {
  if (!htmlString) return '';

  const doc = new DOMParser().parseFromString(htmlString, 'text/html');

  return doc.body.textContent || '';
};

const getStatus = (model, isReadingText, notebooksAndReadingGames) => {
  let status;

  if (isReadingText && notebooksAndReadingGames?.length) {
    const notebooksAndReadingGamesForSection = notebooksAndReadingGames.filter(m => m.parent_section === model.id);
    const assignedStatuses = [model, ...notebooksAndReadingGamesForSection].map(m => m.status);

    if (assignedStatuses.every(s => s === 'turned_in')) {
      status = 'turned_in';
    }
    else if (assignedStatuses.some(s => s === 'in_progress') ||
      (assignedStatuses.some(s => s === 'turned_in') && assignedStatuses.some(s => s === null || s === undefined))) {
      status = 'in_progress';
    }
    else {
      status = null;
    }
  }
  else {
    status = model.status;
  }

  return status;
};

export const AssignmentModels = ({
  className,
  isStudent,
  isReadingText,
  models,
  notebooksAndReadingGames
}) => {
  if (!models?.length) return null;
  return (
    <ul>
      {models.map((model) => {
        const status = getStatus(model, isReadingText, notebooksAndReadingGames);
        const modelLabelString = stripHtml(model.label);

        return (
          <li
            key={`${model.label} ${model.href}`}
            className={`${styles['dropdown-item']} ${className} ${styles.listItem}`}
          >
            {model.before_assignment_start_date && isStudent ? (
              <div>
                {model.label}
                {' '}
                <span className={ASSIGNMENT_DETAILS_TOOLTIP_CLASS} />
              </div>
            ) : model.past_enforced_due_date && isStudent ? (
              <div>
                <span dangerouslySetInnerHTML={{ __html: model.label }} />
                {' '}
                <span className={PAST_DUE_LOCK_TOOLTIP_CLASS} />
              </div>
            ) : (
              <a
                aria-label={`${modelLabelString} (opens in new tab)`}
                className="tw-flex"
                href={model.href}
                rel="noopener noreferrer"
                target="_blank"
              >
                <div dangerouslySetInnerHTML={{ __html: model.label }} />
                <i className="fa fa-external-link purple ml5" />
              </a>
            )}
            {' '}
            <span className={`${styles[status]}`}>{status ? capitalizeWords(status.replace('_', ' ')) : null}</span>
          </li>
        );
      })}
    </ul>
  );
};

AssignmentModels.propTypes = {
  className: PropTypes.string,
  isReadingText: PropTypes.bool,
  isStudent: PropTypes.bool,
  models: PropTypes.arrayOf(
    PropTypes.shape({
      before_assignment_start_date: PropTypes.bool,
      href: PropTypes.string,
      label: PropTypes.string,
      past_enforced_due_date: PropTypes.bool,
      status: PropTypes.string,
    })
  ),
  notebooksAndReadingGames: PropTypes.arrayOf(
    PropTypes.shape({
      before_assignment_start_date: PropTypes.bool,
      href: PropTypes.string,
      label: PropTypes.string,
      parent_section: PropTypes.number,
      status: PropTypes.string,
    })
  ),
};

const ModelDetails = ({
  isReadingText,
  isStudent,
  models,
  notebooksAndReadingGames
}) => {
  if (!models.length) {
    return null;
  }

  return (
    <div className={styles['dropdown-item']}>
      <AssignmentModels
        className={styles.sectionItem}
        isReadingText={isReadingText}
        isStudent={isStudent}
        models={models}
        notebooksAndReadingGames={notebooksAndReadingGames}
      />
    </div>
  );
};

ModelDetails.propTypes = {
  isReadingText: PropTypes.bool,
  isStudent: PropTypes.bool,
  models: PropTypes.arrayOf(PropTypes.shape({
    before_assignment_start_date: PropTypes.bool,
    href: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  notebooksAndReadingGames: PropTypes.arrayOf(
    PropTypes.shape({
      before_assignment_start_date: PropTypes.bool,
      href: PropTypes.string,
      label: PropTypes.string,
      parent_section: PropTypes.number,
      status: PropTypes.string,
    })
  ),
};

const TeacherNote = ({ rowData, isStudent }) => {
  if (!rowData.notes) return null;

  return (
    <div className={styles.noteContainer}>
      {!!isStudent && <span>{rowData.teacher_name}:</span>}

      <div className={styles.notes}>
        {rowData.notes}
      </div>

      <div className={styles.noteBubbleTail}>
        <svg aria-hidden="true" version="1.1" xmlns="http://www.w3.org/2000/svg">
          <line style={{ stroke: '#ccc', strokeWidth: 2 }} x1="0" x2="0" y1="0" y2="15" />
          <line style={{ stroke: '#ccc', strokeWidth: 1 }} x1="0" x2="24" y1="0" y2="15" />
        </svg>
      </div>
    </div>
  );
};

TeacherNote.propTypes = {
  isStudent: PropTypes.bool,
  rowData: PropTypes.shape({
    notes: PropTypes.string,
    teacher_name: PropTypes.string
  }).isRequired
};

TeacherNote.defaultProps = {
  isStudent: true
};

const AssignmentDetails = ({ rowData, isStudent }) => {
  if (!rowData) return null;

  const { Notebook, Section, SlideShow, Challenge, Assessment, Game, SlideGroup, ReadingGame, ReferenceMaterial } = rowData.assignment_models_hash;
  const unitProjects = filterNotebooks(Notebook);
  const [streams, text] = divideSections(Section);
  const readingNotebooksToShow = getReadingNotebooks(Notebook, text);
  const readingGamesToShow = getReadingGames(ReadingGame, text);
  const videoActivity = filterVideoActivities(Notebook, SlideShow);
  const classroomActivity = filterClassroomActivities(SlideShow);

  return (
    <div>
      <AssignmentModels isStudent={isStudent} models={streams} />
      <AssignmentModels isStudent={isStudent} models={SlideGroup} />
      <ModelDetails
        isReadingText
        isStudent={isStudent}
        models={text}
        notebooksAndReadingGames={[...(Notebook || []), ...(ReadingGame || [])]}
      />
      <AssignmentModels isStudent={isStudent} models={readingNotebooksToShow} />
      <AssignmentModels isStudent={isStudent} models={readingGamesToShow} />
      <AssignmentModels isStudent={isStudent} models={Challenge} />
      <AssignmentModels isStudent={isStudent} models={Assessment} />
      <AssignmentModels isStudent={isStudent} models={Game} />
      <ModelDetails isStudent={isStudent} models={unitProjects} />
      <AssignmentModels isStudent={isStudent} models={videoActivity} />
      <AssignmentModels isStudent={isStudent} models={classroomActivity} />
      <AssignmentModels isStudent={isStudent} models={ReferenceMaterial} />
      <TeacherNote isStudent={isStudent} rowData={rowData} />
    </div>
  );
};

export default AssignmentDetails;
